import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Robot, SpinnerGap, ArrowRight, Plus, Trash, Globe } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScraperPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [url, setUrl] = useState("https://www.analvids.com/");
  const [pages, setPages] = useState(1);
  const [downloadThumbs, setDownloadThumbs] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [sources, setSources] = useState([]);
  const [sourceDialog, setSourceDialog] = useState(false);
  const [sourceForm, setSourceForm] = useState({
    name: "", url: "", selector_card: "div.card-scene", selector_title: "div.card-scene__text a",
    selector_thumb: "img", selector_duration: "div.label--time", selector_link: "div.card-scene__text a",
    pagination_pattern: "recommended/{page}?size=99"
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projRes, srcRes] = await Promise.all([
          axios.get(`${API}/projects`, { withCredentials: true }),
          axios.get(`${API}/scraper-sources`, { withCredentials: true }),
        ]);
        setProjects(projRes.data);
        setSources(srcRes.data);
      } catch (err) { console.error(err); }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedProject) return;
    const fetchJobs = async () => {
      try {
        const { data } = await axios.get(`${API}/projects/${selectedProject}/scrape-jobs`, { withCredentials: true });
        setJobs(data);
      } catch (err) { console.error(err); }
    };
    fetchJobs();
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [selectedProject]);

  const handleScrape = async () => {
    if (!selectedProject) { toast.error("Select a project first"); return; }
    setScraping(true);
    try {
      const { data } = await axios.post(`${API}/projects/${selectedProject}/scrape`, { url, pages, download_thumbnails: downloadThumbs }, { withCredentials: true });
      toast.success(data.message);
    } catch (err) { toast.error("Scraping failed"); }
    finally { setScraping(false); }
  };

  const handleAddSource = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/scraper-sources`, sourceForm, { withCredentials: true });
      toast.success("Source added");
      setSourceDialog(false);
      setSourceForm({ name: "", url: "", selector_card: "div.card-scene", selector_title: "div.card-scene__text a", selector_thumb: "img", selector_duration: "div.label--time", selector_link: "div.card-scene__text a", pagination_pattern: "recommended/{page}?size=99" });
      const { data } = await axios.get(`${API}/scraper-sources`, { withCredentials: true });
      setSources(data);
    } catch { toast.error("Failed to add source"); }
  };

  const handleDeleteSource = async (id) => {
    try {
      await axios.delete(`${API}/scraper-sources/${id}`, { withCredentials: true });
      toast.success("Source deleted");
      setSources(prev => prev.filter(s => s.id !== id));
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="scraper-page" className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>Content Scraper</h1>
        <p className="text-sm text-[#A1A1AA] mt-1">Scrape content from external sources into your projects</p>
      </div>

      {/* Scrape Form */}
      <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-5 space-y-4">
        <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Quick Scrape</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1.5 block">Target Project</label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger data-testid="scraper-project-select" className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10">
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent className="bg-[#141417] border-[#2A2A2E] text-white">
                {projects.map(p => <SelectItem key={p.id} value={p.id} className="hover:bg-[#1E1E22]">{p.name} ({p.domain})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1.5 block">Source URL</label>
            <div className="flex gap-2">
              <Input data-testid="scraper-url-input" value={url} onChange={e => setUrl(e.target.value)} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10 flex-1" />
              {sources.length > 0 && (
                <Select onValueChange={v => setUrl(v)}>
                  <SelectTrigger className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10 w-40"><SelectValue placeholder="Presets" /></SelectTrigger>
                  <SelectContent className="bg-[#141417] border-[#2A2A2E] text-white">
                    {sources.map(s => <SelectItem key={s.id} value={s.url} className="hover:bg-[#1E1E22]">{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1.5 block">Pages</label>
            <Input data-testid="scraper-pages-input" type="number" min={1} max={10} value={pages} onChange={e => setPages(parseInt(e.target.value) || 1)} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-[#A1A1AA] cursor-pointer">
              <input type="checkbox" checked={downloadThumbs} onChange={e => setDownloadThumbs(e.target.checked)} className="rounded-sm" />
              Download thumbnails
            </label>
          </div>
          <div className="flex items-end">
            <Button data-testid="start-scrape-btn" onClick={handleScrape} disabled={scraping || !selectedProject} className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm h-10 px-6 font-semibold w-full disabled:opacity-50">
              {scraping ? <><SpinnerGap size={14} className="mr-1.5 animate-spin" /> Scraping...</> : <><Robot size={14} className="mr-1.5" /> Start Scrape</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Scraper Sources */}
      <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm">
        <div className="px-5 py-4 border-b border-[#2A2A2E] flex items-center justify-between">
          <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Scraper Sources</h3>
          <Button data-testid="add-source-btn" onClick={() => setSourceDialog(true)} className="bg-[#4361EE] hover:bg-[#3651DE] text-white rounded-sm h-8 px-3 text-xs font-semibold">
            <Plus size={12} className="mr-1" /> Add Source
          </Button>
        </div>
        <div className="divide-y divide-[#2A2A2E]">
          {sources.length === 0 ? (
            <div className="px-5 py-6 text-center text-sm text-[#555]">No saved sources. Add a source URL to reuse later.</div>
          ) : sources.map(src => (
            <div key={src.id} className="px-5 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm text-white font-medium flex items-center gap-2">{src.name} <Globe size={12} className="text-[#4361EE]" /></div>
                <div className="text-xs text-[#A1A1AA] mt-0.5">{src.url}</div>
                <div className="text-[10px] text-[#555] mt-0.5">Card: {src.selector_card} &middot; Pagination: {src.pagination_pattern}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => setUrl(src.url)} className="bg-[#141417] border border-[#2A2A2E] hover:border-[#4361EE] text-white rounded-sm h-7 px-3 text-[10px]">Use</Button>
                <button data-testid={`delete-source-${src.id}`} onClick={() => handleDeleteSource(src.id)} className="p-1.5 text-[#A1A1AA] hover:text-[#E53935] transition-colors"><Trash size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Jobs */}
      <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm">
        <div className="px-5 py-4 border-b border-[#2A2A2E] flex items-center justify-between">
          <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Job History</h3>
          {selectedProject && (
            <button data-testid="view-project-btn" onClick={() => navigate(`/projects/${selectedProject}`)} className="text-xs text-[#4361EE] hover:underline flex items-center gap-1">View Project <ArrowRight size={12} /></button>
          )}
        </div>
        <div className="divide-y divide-[#2A2A2E]">
          {jobs.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-[#555]">{selectedProject ? "No jobs for this project" : "Select a project to see jobs"}</div>
          ) : jobs.map(job => (
            <div key={job.id} className="px-5 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm text-white font-medium capitalize">{job.type}</div>
                <div className="text-xs text-[#A1A1AA]">{job.source_url || ""} &middot; {new Date(job.created_at).toLocaleString()}</div>
              </div>
              <div className="text-right">
                <span className={`text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-wider font-bold ${job.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : job.status === 'running' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-[#E53935]/10 text-[#E53935]'}`}>{job.status}</span>
                {job.new_items !== undefined && <div className="text-xs text-[#A1A1AA] mt-1">{job.new_items} new / {job.total_scraped} total</div>}
                {job.translated_count !== undefined && <div className="text-xs text-[#A1A1AA] mt-1">{job.translated_count} translated</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Source Dialog */}
      <Dialog open={sourceDialog} onOpenChange={setSourceDialog}>
        <DialogContent className="bg-[#141417] border-[#2A2A2E] text-white max-w-lg rounded-sm">
          <DialogHeader><DialogTitle className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Add Scraper Source</DialogTitle></DialogHeader>
          <form onSubmit={handleAddSource} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Source Name</label><Input data-testid="source-name-input" value={sourceForm.name} onChange={e => setSourceForm({...sourceForm, name: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" required /></div>
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Source URL</label><Input data-testid="source-url-input" value={sourceForm.url} onChange={e => setSourceForm({...sourceForm, url: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" required /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#555] font-bold mb-1 block">Card Selector</label><Input value={sourceForm.selector_card} onChange={e => setSourceForm({...sourceForm, selector_card: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-[#A1A1AA] rounded-sm h-9 text-xs" /></div>
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#555] font-bold mb-1 block">Title Selector</label><Input value={sourceForm.selector_title} onChange={e => setSourceForm({...sourceForm, selector_title: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-[#A1A1AA] rounded-sm h-9 text-xs" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#555] font-bold mb-1 block">Pagination Pattern</label><Input value={sourceForm.pagination_pattern} onChange={e => setSourceForm({...sourceForm, pagination_pattern: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-[#A1A1AA] rounded-sm h-9 text-xs" /></div>
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#555] font-bold mb-1 block">Duration Selector</label><Input value={sourceForm.selector_duration} onChange={e => setSourceForm({...sourceForm, selector_duration: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-[#A1A1AA] rounded-sm h-9 text-xs" /></div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={() => setSourceDialog(false)} className="text-[#A1A1AA] hover:text-white rounded-sm">Cancel</Button>
              <Button data-testid="source-submit-btn" type="submit" className="bg-[#4361EE] hover:bg-[#3651DE] text-white rounded-sm px-6 font-semibold">Save Source</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
